import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { useAtom } from 'jotai';
import { useFormContext } from 'react-hook-form';

import JsonInput from '@/components/JsonInput';
import LoadingIndicator from '@/components/LoadingIndicator';

import { useLogSourcesForm } from './LogSourcesCreate.hooks';
import {
  filterSourceAtom,
  hostNameAtom,
  orgNameAtom,
  searchQueryAtom,
  selectedSourceCountAtom
} from './LogSourcesCreate.store';
import type { LogSourceModel } from './LogSourcesCreate.types';
import LogSourceRow from './LogSourcesCreateRow';

const LogSourcesCreateForm = () => {
  const { register } = useFormContext();
  const [searchQuery] = useAtom(searchQueryAtom);
  const [filter] = useAtom(filterSourceAtom);
  const [hostName] = useAtom(hostNameAtom);
  const [orgName] = useAtom(orgNameAtom);
  const [selectedSourceCount] = useAtom(selectedSourceCountAtom);

  const {
    logSourcesQuery,
    logSourcesByDeviceIdQuery,
    handleKeyDown,
    handleSearchChange,
    toggleFilter,
    filteredLogSources
  } = useLogSourcesForm();

  return (
    <>
      <LoadingIndicator
        isLoading={
          logSourcesByDeviceIdQuery?.isRefetching ||
          logSourcesQuery.isRefetching
        }
      />
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 6 }}>
            <InputLabel htmlFor="device-name-input">Organization</InputLabel>
            <FormControl fullWidth>
              <TextField name="organization_name" value={orgName} disabled />
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 6 }}>
            <InputLabel htmlFor="device-name-input">Device Name</InputLabel>
            <FormControl fullWidth>
              <TextField name="device_name" value={hostName} disabled />
            </FormControl>
          </Grid>
        </Grid>
      </Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 12, md: 12 }} style={{ marginTop: '1rem' }}>
          <Box style={{ display: 'flex' }}>
            <FormControl fullWidth>
              {/* Search Input */}
              <TextField
                type="text"
                placeholder="Search by product, name, or data stream..."
                value={searchQuery}
                onKeyDown={handleKeyDown}
                onChange={handleSearchChange}
                style={{ width: '100%' }}
              />
            </FormControl>
            {/* Toggle Filter Button */}

            <Button
              style={{
                whiteSpace: 'nowrap',
                marginLeft: '0.875rem',
                paddingLeft: '1.5rem',
                paddingRight: '1.5rem'
              }}
              variant="outlined"
              color="primary"
              type="button"
              onClick={toggleFilter}
            >
              Show{' '}
              {filter === 'all' ? `Selected (${selectedSourceCount})` : 'All'}
            </Button>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, sm: 12, md: 12 }}>
          <TableContainer
            style={{ maxHeight: '19.75rem', minHeight: '19.75rem' }}
            component={Paper}
          >
            <LoadingIndicator isLoading={logSourcesQuery.isLoading} />
            <Table aria-label="collapsible table">
              <TableBody>
                {filteredLogSources.length > 0 ? (
                  filteredLogSources.map((row: LogSourceModel) => (
                    <LogSourceRow key={row.name} row={row} />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={999} sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No log sources found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <Grid size={{ xs: 12, sm: 12, md: 12 }}>
          <InputLabel htmlFor="device-name-input">
            Input Configuration
          </InputLabel>
          <FormControl fullWidth>
            <JsonInput
              {...register(`input_configuration`)}
              rows={2}
              name="input_configuration"
              label="Input Configuration"
            />
          </FormControl>
        </Grid>
      </Grid>
    </>
  );
};

export default LogSourcesCreateForm;
